import { Paths, PathValue } from "@mctypes/global.types";
import { useEffect } from "react";
import { Button, Flex, Paper, Stack, Text, Title, Box } from "@mantine/core";
import { HSpace, McInputNew, McTextAreaNew } from "../../../../../../../components";
import { IGKRapport, IGKRecoveryTestResultater } from "@mcapps/shared/reportSchemas/gkReportSchema";
import ReportDocxImageContainer from "../../../../ReportBoard/ReportDocxImageContainer";
import { beregnetSluttkonsentrasjon } from "./recoveryTestUtils";
const RecoveryTest: React.FC<{
  onUpdateEditData: <P extends Paths<IGKRapport, 4>>(path: P, value: PathValue<IGKRapport, P>) => void;
  data: IGKRecoveryTestResultater;
}> = ({ data, onUpdateEditData }) => {
  useEffect(() => {
    utdataBeregninger();
  }, [data.inndata.maaleresultater, data.inndata.maaleresultater.length]);
  const addNewRow = () => {
    const newRecoveryTestMaalepunkt = {
      maalepunktNr: "",
      maaleresultatStart: null,
      maaleresultatSlutt: null,
      maaletid: null,
    };
    const updatedMaaleresultater = [...data.inndata.maaleresultater, newRecoveryTestMaalepunkt];
    onUpdateEditData("testResultater.recoveryTest.inndata.maaleresultater", updatedMaaleresultater);
    return updatedMaaleresultater;
  };
  const removeLastRow = () => {
    const updatedMaaleresultater = [...data.inndata.maaleresultater];
    if (updatedMaaleresultater.length > 0) {
      updatedMaaleresultater.pop(); // Remove the last element
      onUpdateEditData("testResultater.recoveryTest.inndata.maaleresultater", updatedMaaleresultater);
    }
  };
  const updateMaaleresultater = (resultat: string | number, i: number, parameter: string) => {
    const maaleresultater = [...data.inndata.maaleresultater];
    maaleresultater[i][parameter] = resultat;
    onUpdateEditData("testResultater.recoveryTest.inndata.maaleresultater", maaleresultater);
  };
  const utdataBeregninger = () => {
    if (data.inndata.maaleresultater.length === 0) {
      return;
    } else {
      const teoretiskUttynningstid = beregnetSluttkonsentrasjon(
        data.inndata.luftskifterIRommet,
        data.inndata.bakgrunnskonsentrasjonFoerTest,
        data.inndata.startkonsentrasjon,
      );
      const utdata = {
        teoretiskUttynningstid: teoretiskUttynningstid,
      };
      onUpdateEditData("testResultater.recoveryTest.utdata", utdata);
    }
  };
  return (
    <Paper withBorder p="sm" radius="sm" bg="#fdfdff" w="75%">
      <Stack gap="xs">
        <Title order={4} fw={400}>
          Recovery-test
        </Title>
        <Stack gap={0}>
          <McInputNew
            label={
              <>
                {" "}
                Fyll inn bakgrunnskonsentrasjon før test &#40;antall/m<sup>3</sup>&#41;:{" "}
              </>
            }
            value={data.inndata.bakgrunnskonsentrasjonFoerTest}
            onBlur={(e) =>
              onUpdateEditData(
                "testResultater.recoveryTest.inndata.bakgrunnskonsentrasjonFoerTest",
                Number(e.target.value),
              )
            }
            normalizeNumbers={true}
            type="text"
            style={{ width: "25%" }}
          />
        </Stack>
        <Flex direction="column" mt="lg">
          <McInputNew
            label="Fyll inn luftskifter i rommet (ACH):"
            value={data.inndata.luftskifterIRommet}
            onBlur={(e) =>
              onUpdateEditData("testResultater.recoveryTest.inndata.luftskifterIRommet", Number(e.target.value))
            }
            normalizeNumbers={true}
            type="text"
            style={{ width: "25%" }}
          />
        </Flex>
        <Flex direction="column" mt="lg">
          <McInputNew
            label={
              <>
                {" "}
                Fyll inn konsentrasjon ved start av test &#40;antall/m<sup>3</sup>&#41;:{" "}
              </>
            }
            value={data.inndata.startkonsentrasjon}
            onBlur={(e) =>
              onUpdateEditData("testResultater.recoveryTest.inndata.startkonsentrasjon", Number(e.target.value))
            }
            normalizeNumbers={true}
            type="text"
            style={{ width: "25%" }}
          />
        </Flex>
        <Box mt="lg">
          <b>Hvor mange målinger skal utføres? &#40;-&#41;:</b>
          <Flex>
            <Button color="green" onClick={addNewRow}>
              Legg til rad
            </Button>
            <HSpace />
            <Button variant="danger-filled" onClick={removeLastRow}>
              Fjern siste rad
            </Button>
          </Flex>
        </Box>
        {data.inndata.maaleresultater.length > 0 && (
          <Flex direction="column" mt="lg">
            <b>Fyll inn måleresultater:</b>
            <table>
              <thead>
                <tr>
                  <th>Målepunktnummer &#40;-&#41;</th>
                  <th>
                    Måleresultat ved start &#40;antall/m<sup>3</sup>&#41;
                  </th>
                  <th>
                    Måleresultat ved slutt &#40;antall/m<sup>3</sup>&#41;
                  </th>
                  <th>Måletid &#40;s&#41;</th>
                </tr>
              </thead>
              {data.inndata.maaleresultater.map((resultat, i) => (
                <tbody key={i}>
                  <tr>
                    <td>
                      <McInputNew
                        value={resultat.maalepunktNr}
                        type="text"
                        onChange={(e) => updateMaaleresultater(e.target.value, i, "maalepunktNr")}
                        placeholder="Skriv inn målepunktnummer"
                      />
                    </td>
                    <td>
                      <McInputNew
                        value={resultat.maaleresultatStart}
                        normalizeNumbers={true}
                        type="text"
                        onBlur={(e) => updateMaaleresultater(Number(e.target.value), i, "maaleresultatStart")}
                        placeholder="Skriv inn startverdi"
                      />
                    </td>
                    <td>
                      <McInputNew
                        value={resultat.maaleresultatSlutt}
                        normalizeNumbers={true}
                        type="text"
                        onBlur={(e) => updateMaaleresultater(Number(e.target.value), i, "maaleresultatSlutt")}
                        placeholder="Skriv inn sluttverdi"
                      />
                    </td>
                    <td>
                      <McInputNew
                        value={resultat.maaletid}
                        normalizeNumbers={true}
                        type="text"
                        onBlur={(e) => updateMaaleresultater(Number(e.target.value), i, "maaletid")}
                        placeholder="Skriv in måletid"
                      />
                    </td>
                  </tr>
                </tbody>
              ))}
            </table>
            <Flex justify="space-between">
              <Text mb={0}>
                Teoretisk uttynningstid &#40;t&#41; ={" "}
                <b>
                  {" "}
                  {beregnetSluttkonsentrasjon(
                    data.inndata.luftskifterIRommet,
                    data.inndata.bakgrunnskonsentrasjonFoerTest,
                    data.inndata.startkonsentrasjon,
                  )?.toFixed(2) ?? ""}
                </b>
              </Text>
            </Flex>
          </Flex>
        )}
        {data.inndata.maaleresultater.length > 0 && (
          <Box mt="lg">
            <ReportDocxImageContainer docxImageTag="recoveryTest" title="Målepunkter i rommet" />
            <div style={{ width: "50%" }}>
              <McTextAreaNew
                label="Kommentar:"
                value={data.inndata.kommentar}
                onChange={(e) => onUpdateEditData("testResultater.recoveryTest.inndata.kommentar", e.target.value)}
              />
            </div>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
export default RecoveryTest;
